import PageHeader from '@/components/common/PageHeader';
import { getQuoteLineItemsApi, getQuoteDetailFromDataverse } from '@/features/quotes-management/api/dataverse';
import { getCustomerDetailByIdApi } from '@/features/customers/api';
import { IDataverseQuote } from '@/features/quotes-management/types';
import { generateOrderAcknowledgementPDF, IPDFHeaderDates } from '@/utils/orderAcknowledgementPDF';
import dayjs from 'dayjs';
import { REGION_ALL } from '@/utils/constant/appConstant';
import { exportToCSV, generateCSVFilename, formatDateForCSV } from '@/utils/csv';
import { formatDealerAmount } from '@/utils/currency';
import { Button, Modal, Spin } from 'antd';
import { IoCloudDownloadOutline } from 'react-icons/io5';
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { OrderStatusModal } from '../components/modals/OrderStatusModal';
import { EvolutionJobNumberModal } from '../components/modals/EvolutionJobNumberModal';
import { ResendPaymentLinkModal } from '../components/modals/ResendPaymentLinkModal';
import { OrderStatistics } from '../components/orderStatistics';
import { OrderTable } from '../components/table';
import { OrderFilters } from '../components';
import { DEFAULT_PAGE_SIZE, DEFAULT_STARTING_PAGE, ORDER_STATUS_OPTIONS, OrderStateCode, OrderStatus } from '../constants';
import {
  useAllOrderCountsByStatus,
  useExportOrders,
  useOrderListFromDataverse,
  useOutstandingPaymentsValue,
  useTotalOrdersCount,
  useUpdateOrder,
  useFulfillSalesOrder,
  useCancelSalesOrder,
} from '../hooks';
import { createPaymentApi } from '../api/dataverse';
import { IDataverseOrder, IOrderQueryParams } from '../types';
import { combineFilterQueries, generateOrdersSearchQuery, generateStatusFilterQuery, generateRegionFilterQuery, generateAccountFilterQuery, generatePaymentStatusFilterQuery, generateDateRangeFilterQuery, generateProjectTypeFilterQuery, getOrderNumber } from '../utils';
import { useGetCurrentUser } from '@/hooks/app';

export const OrdersManagementPage: React.FC = () => {
  const [queryParams, setQueryParams] = useState<IOrderQueryParams>({
    current: DEFAULT_STARTING_PAGE,
    pageSize: DEFAULT_PAGE_SIZE || 10,
    filter: '',
  });

  const { data: currentUser } = useGetCurrentUser();
  const isRegionAll = Boolean(currentUser?.currentUser?.dogma_region && currentUser?.currentUser?.dogma_region === REGION_ALL) ?? false;
  const deafultRegionFilters = isRegionAll ? "" : generateRegionFilterQuery(currentUser?.currentUser?.dogma_region);
  const { data: orderListResponse, isLoading } = useOrderListFromDataverse({
    queryParams: {
      ...queryParams,
      filter: queryParams.filter ? queryParams.filter : `&$filter=${deafultRegionFilters}`
    },
    enabled: Boolean(currentUser?.currentUser?.systemuserid)
  });
  const orders = orderListResponse?.value || [];

  const [currentSearchText, setCurrentSearchText] = useState<string>("");
  const [currentStatus, setCurrentStatus] = useState<OrderStatus | null>(null);
  const [currentRegion, setCurrentRegion] = useState<number | null>(null);
  const { data: statusCountsData } = useAllOrderCountsByStatus(currentRegion);
  const { data: totalOrdersCountData, isLoading: isTotalCountLoading } = useTotalOrdersCount(currentRegion);
  const { data: outstandingPaymentsData, isLoading: isOutstandingPaymentsLoading } = useOutstandingPaymentsValue(currentRegion);

  const totalOrdersCount = totalOrdersCountData?.value[0]?.count ?? 0;
  const outstandingPaymentsValue = outstandingPaymentsData?.value[0]?.totalValue ?? 0;

  const [currentAccount, setCurrentAccount] = useState<string | null>(null);
  const [currentPaymentStatus, setCurrentPaymentStatus] = useState<number | null>(null);
  const [currentDateRange, setCurrentDateRange] = useState<[string, string] | null>(null);
  const [currentProjectType, setCurrentProjectType] = useState<number | null>(null);

  // Modal states
const [orderStatusModalOpen, setOrderStatusModalOpen] = useState(false);
  const [evolutionJobModalOpen, setEvolutionJobModalOpen] = useState(false);
  const [resendPaymentModalOpen, setResendPaymentModalOpen] = useState(false);
  const [isSendingPayment, setIsSendingPayment] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<IDataverseOrder | null>(null);

  // PDF generation state
  const [pdfProgressModal, setPdfProgressModal] = useState(false);
  const [pdfProgressText, setPdfProgressText] = useState('');

  // Mutation hooks
  const exportOrdersMutation = useExportOrders();
  const updateOrderMutation = useUpdateOrder();
  const fulfillSalesOrderMutation = useFulfillSalesOrder();
  const cancelSalesOrderMutation = useCancelSalesOrder();

  const isStatsLoading = isTotalCountLoading || isOutstandingPaymentsLoading;

  React.useEffect(() => {
    if (currentUser?.currentUser?.dogma_region && !isRegionAll) {
      setCurrentRegion(currentUser?.currentUser?.dogma_region)
    }
  }, [currentUser?.currentUser?.dogma_region])

  const resetToFirstPage = (filter: string) => {
    setQueryParams({ current: DEFAULT_STARTING_PAGE, pageSize: queryParams.pageSize, filter });
  };

  const handleTableChange = (current: number, pageSize: number) => {
    setQueryParams((prev) => ({ ...prev, current, pageSize }));
  };

  const buildFilters = (overrides: {
    searchText?: string;
    status?: OrderStatus | null;
    region?: number | null;
    account?: string | null;
    paymentStatus?: number | null;
    dateRange?: [string, string] | null;
    projectType?: number | null;
  }) => {
    const searchText = 'searchText' in overrides ? (overrides.searchText ?? '') : currentSearchText;
    const status = 'status' in overrides ? overrides.status : currentStatus;
    const region = 'region' in overrides ? overrides.region : currentRegion;
    const account = 'account' in overrides ? overrides.account : currentAccount;
    const paymentStatus = 'paymentStatus' in overrides ? overrides.paymentStatus : currentPaymentStatus;
    const dateRange = 'dateRange' in overrides ? overrides.dateRange : currentDateRange;
    const projectType = 'projectType' in overrides ? overrides.projectType : currentProjectType;

    const searchQuery = searchText ? generateOrdersSearchQuery(searchText).replace("&$filter=", "") : "";
    const statusQuery = status !== null && status !== undefined ? generateStatusFilterQuery(status) : "";
    const regionQuery = region !== null && region !== undefined ? generateRegionFilterQuery(region) : "";
    const accountQuery = account ? generateAccountFilterQuery(account) : "";
    const paymentStatusQuery = paymentStatus !== null && paymentStatus !== undefined ? generatePaymentStatusFilterQuery(paymentStatus) : "";
    const dateRangeQuery = dateRange ? generateDateRangeFilterQuery(dateRange[0], dateRange[1]) : "";
    const projectTypeQuery = projectType !== null && projectType !== undefined ? generateProjectTypeFilterQuery(projectType) : "";

    const combined = combineFilterQueries([
      searchQuery ? `(${searchQuery})` : "",
      statusQuery,
      regionQuery,
      accountQuery,
      paymentStatusQuery,
      dateRangeQuery,
      projectTypeQuery,
    ]);
    return combined ? `&$filter=${combined}` : '';
  };

  const handleSearchTextChange = (searchText: string) => {
    setCurrentSearchText(searchText);
    resetToFirstPage(buildFilters({ searchText }));
  };

  const handleStatusFilterChange = (status: OrderStatus | null | undefined) => {
    setCurrentStatus(status || null);
    resetToFirstPage(buildFilters({ status: status || null }));
  };

  const handleRegionFilterChange = (region: number | null | undefined) => {
    setCurrentRegion(region || null);
    resetToFirstPage(buildFilters({ region: region || null }));
  };

  const handleAccountFilterChange = (accountId: string | null) => {
    setCurrentAccount(accountId);
    resetToFirstPage(buildFilters({ account: accountId }));
  };

  const handlePaymentStatusFilterChange = (paymentStatus: number | null) => {
    setCurrentPaymentStatus(paymentStatus);
    resetToFirstPage(buildFilters({ paymentStatus }));
  };

  const handleDateRangeFilterChange = (dateRange: [string, string] | null) => {
    setCurrentDateRange(dateRange);
    resetToFirstPage(buildFilters({ dateRange }));
  };

  const handleProjectTypeFilterChange = (projectType: number | null) => {
    setCurrentProjectType(projectType);
    resetToFirstPage(buildFilters({ projectType }));
  };

  const handleApplyAllFilters = ({
    status,
    region,
    account,
    paymentStatus,
    dateRange,
    projectType,
  }: {
    status: OrderStatus | null;
    region: number | null;
    account: string | null;
    paymentStatus: number | null;
    dateRange: [string, string] | null;
    projectType: number | null;
  }) => {
    setCurrentStatus(status);
    setCurrentRegion(region);
    setCurrentAccount(account);
    setCurrentPaymentStatus(paymentStatus);
    setCurrentDateRange(dateRange);
    setCurrentProjectType(projectType);
    resetToFirstPage(buildFilters({ status, region, account, paymentStatus, dateRange, projectType }));
  };

  // Clear all filters
  const handleClearAllFilters = () => {
    setCurrentSearchText('');
    setCurrentStatus(null);
    setCurrentRegion(isRegionAll ? null : (currentUser?.currentUser?.dogma_region || null));
    setCurrentAccount(null);
    setCurrentPaymentStatus(null);
    setCurrentDateRange(null);
    setCurrentProjectType(null);
    resetToFirstPage(deafultRegionFilters ? `&$filter=${deafultRegionFilters}` : '');
  };

  // Get count for each status
  const getCountForStatus = (statusCode: number): number => {
    const statusCount = statusCountsData?.value?.find((sc: any) => sc.statuscode === statusCode);
    return statusCount?.count || 0;
  };

  // Status filter options with counts
  const statusFilterOptions = useMemo(() => {
    return ORDER_STATUS_OPTIONS.map((option) => ({
      value: option.value,
      label: `${option.label} (${getCountForStatus(option.value)})`,
    }));
  }, [statusCountsData]);

  // Modal handlers
const handleOpenOrderStatusModal = (order: IDataverseOrder) => {
    setSelectedOrder(order);
    setOrderStatusModalOpen(true);
  };

const handleCloseOrderStatusModal = () => {
    setOrderStatusModalOpen(false);
    setSelectedOrder(null);
  };

  const handleOpenEvolutionJobModal = (order: IDataverseOrder) => {
    setSelectedOrder(order);
    setEvolutionJobModalOpen(true);
  };

  const handleCloseEvolutionJobModal = () => {
    setEvolutionJobModalOpen(false);
    setSelectedOrder(null);
  };

  const handleSaveEvolutionJobNumber = (orderId: string, evolutionJobNumber: string) => {
    updateOrderMutation.mutate(
      { id: orderId, dogma_evolutionjobnumber: evolutionJobNumber },
      { onSuccess: () => handleCloseEvolutionJobModal() }
    );
  };

  const handleOpenResendPaymentModal = (order: IDataverseOrder) => {
    setSelectedOrder(order);
    setResendPaymentModalOpen(true);
  };

  const handleResendPaymentLink = async (destination: 'email' | 'sms') => {
    if (!selectedOrder) return;
    setIsSendingPayment(true);
    try {
      const exchangeRate = selectedOrder.quoteid?.dogma_exchangerate ?? selectedOrder.quoteid?.dogma_DealersCurrency?.dogma_exchangerate ?? 1;
      const vssTotal = selectedOrder.dogma_vsstotalamount ?? 0;
      await createPaymentApi({
        total: Math.round(vssTotal * exchangeRate * 100),
        currency: selectedOrder.quoteid?.dogma_DealersCurrency?.dogma_isocurrencycode ?? 'GBP',
        userPaymentId: `${getOrderNumber(selectedOrder)}-${selectedOrder['_customerid_value@OData.Community.Display.V1.FormattedValue'] ?? ''}`,
        email: selectedOrder.customerid_contact?.emailaddress1 ?? '',
        phone: selectedOrder.customerid_contact?.telephone1 ?? '',
        orderid: selectedOrder.salesorderid,
        destination,
      });
      toast.success('Payment link sent successfully');
      setResendPaymentModalOpen(false);
      setSelectedOrder(null);
    } catch {
      toast.error('Failed to send payment link');
    } finally {
      setIsSendingPayment(false);
    }
  };

const handleUpdateOrderStatus = (orderId: string, orderStatus: OrderStatus, evolutionJobNumber?: string) => {
    // If status is Completed, use the FulfillSalesOrder API
    if (orderStatus === OrderStatus.Completed) {
      const payload = {
        OrderClose: {
          subject: "Order Fulfilled",
          "salesorderid@odata.bind": `/salesorders(${selectedOrder?.salesorderid})`
        },
        Status: 100001
      };

      fulfillSalesOrderMutation.mutate(
        payload,
        {
          onSuccess: () => {
            handleCloseOrderStatusModal();
          },
        }
      );
      return;
    }

    // If status is Cancelled, use the CancelSalesOrder API
    if (orderStatus === OrderStatus.Cancelled) {
      const payload: any = {
        OrderClose: {
          "@odata.type": "#Microsoft.Dynamics.CRM.orderclose",
          "actualend": new Date().toISOString(),
          "description": null,
          "salesorderid@odata.bind": `/salesorders(${selectedOrder?.salesorderid})`
        },
        Status: 4
      };

      cancelSalesOrderMutation.mutate(
        payload,
        {
          onSuccess: () => {
            handleCloseOrderStatusModal();
          },
        }
      );
      return;
    }

    // For other statuses (IN_PRODUCTION, NEW), use the regular update API
    let payload: any = {}
    if (orderStatus === OrderStatus.IN_PRODUCTION) {
      payload = {
        statecode: OrderStateCode.Active,
        statuscode: 736000001,
        dogma_evolutionjobnumber: evolutionJobNumber,
      }
    }
    updateOrderMutation.mutate(
      { ...payload, id: orderId },
      {
        onSuccess: () => {
          handleCloseOrderStatusModal();
        },
      }
    );
  };

  const handleExportCSV = () => {
    exportOrdersMutation.mutate(queryParams.filter || '', {
      onSuccess: (data) => {
        const rows = data?.value || [];
        const csvData = rows.map((o: IDataverseOrder) => ({
          'Order Number': getOrderNumber(o) || '-',
          'Customer': o['_customerid_value@OData.Community.Display.V1.FormattedValue'] || '-',
          'Company': o.quoteid?.['_dogma_customer_value@OData.Community.Display.V1.FormattedValue'] || '-',
          'Project Reference': o.quoteid?.dogma_projectreference || '-',
          'Order Status': o['statuscode@OData.Community.Display.V1.FormattedValue'] || '-',
          'Payment Status': o['dogma_paymentstatus@OData.Community.Display.V1.FormattedValue'] || '-',
          'Date Ordered': o.createdon ? formatDateForCSV(o.createdon) : '-',
          'Total Amount': o.totalamount != null ? formatDealerAmount(o.totalamount, o.quoteid?.dogma_DealersCurrency, o.quoteid?.dogma_exchangerate ?? o.quoteid?.dogma_DealersCurrency?.dogma_exchangerate ?? 1) : '-',
        }));
        const columns = [
          { key: 'Order Number' as const, header: 'Order Number' },
          { key: 'Customer' as const, header: 'Customer' },
          { key: 'Company' as const, header: 'Company' },
          { key: 'Project Reference' as const, header: 'Project Reference' },
          { key: 'Order Status' as const, header: 'Order Status' },
          { key: 'Payment Status' as const, header: 'Payment Status' },
          { key: 'Date Ordered' as const, header: 'Date Ordered' },
          { key: 'Total Amount' as const, header: 'Total Amount' },
        ];
        exportToCSV(csvData, columns, generateCSVFilename('orders'));
      },
    });
  };

  const handleDownloadPDF = async (order: IDataverseOrder) => {
    const quoteId = order.quoteid?.quoteid;
    if (!quoteId) return;

    setPdfProgressText('Fetching order items...');
    setPdfProgressModal(true);

    try {
      const accountId = order.quoteid?._dogma_customer_value;
      const [lineItemsResponse, quoteDetail, accountDetail] = await Promise.all([
        getQuoteLineItemsApi(quoteId),
        getQuoteDetailFromDataverse(quoteId),
        accountId ? getCustomerDetailByIdApi(accountId) : Promise.resolve(null),
      ]);
      const lineItems = lineItemsResponse?.value || [];

      setPdfProgressText('Generating PDF...');

      const quoteData: IDataverseQuote = {
        ...quoteDetail,
        quotenumber: getOrderNumber(order),
        createdon: order.createdon,
        dogma_vsssubtotal: order.dogma_vsssubtotal,
        dogma_vssvatamount: order.dogma_vssvatamount,
        dogma_vsstotalamount: order.dogma_vsstotalamount,
        dogma_vssdiscountamount: order.dogma_vssdiscountamount || 0,
        dogma_customer: accountDetail ? {
          accountid: accountDetail.accountid,
          name: accountDetail.name,
          address1_composite: accountDetail.address1_composite,
          telephone1: accountDetail.telephone1,
          primarycontactid: accountDetail.primarycontactid,
        } : quoteDetail.dogma_customer,
      };

      const processedDate = order.createdon ? dayjs(order.createdon).format('DD-MMM-YYYY') : '';
      const headerDates: IPDFHeaderDates = {
        orderDate: order.quoteid?.createdon ? dayjs(order.quoteid.createdon).format('DD-MMM-YYYY') : '',
        processedDate,
        productionDate: order.createdon ? dayjs(order.createdon).add(15, 'day').format('DD-MMM-YYYY') : '',
        printedDate: dayjs().format('DD-MMM-YYYY'),
        deliveryDate: order.requestdeliveryby ? dayjs(order.requestdeliveryby).format('DD-MMM-YYYY') : '',
        evolutionJobNumber: order.dogma_evolutionjobnumber ?? undefined,
      };

      await generateOrderAcknowledgementPDF(quoteData, lineItems, headerDates, 'Order Acknowledgement');
    } catch {
      // error handled silently; toast shown by generateOrderAcknowledgementPDF if needed
    } finally {
      setPdfProgressModal(false);
      setPdfProgressText('');
    }
  };

  return (
    <div className="p-4 space-y-4">
      <PageHeader
        title="Orders"
        subtitle="Track and manage all customer orders"
        breadcrumbs={[
          { label: 'Home', path: '/' },
          { label: 'Orders' },
        ]}
        actions={
          <Button
            icon={<IoCloudDownloadOutline />}
            loading={exportOrdersMutation.isPending}
            onClick={handleExportCSV}
          >
            Export CSV
          </Button>
        }
      />


        {/* Stats Cards */}
        <OrderStatistics
          statusCounts={statusCountsData?.value}
          totalCount={totalOrdersCount}
          totalValue={outstandingPaymentsValue}
          loading={isStatsLoading}
        />

        {/* Search and Filter */}
        <OrderFilters
          currentSearchText={currentSearchText}
          currentStatus={currentStatus}
          currentRegion={currentRegion}
          currentAccount={currentAccount}
          currentPaymentStatus={currentPaymentStatus}
          currentDateRange={currentDateRange}
          currentProjectType={currentProjectType}
          isRegionAll={isRegionAll}
          userRegion={currentUser?.currentUser?.dogma_region}
          statusFilterOptions={statusFilterOptions}
          onSearchChange={handleSearchTextChange}
          onStatusChange={handleStatusFilterChange}
          onRegionChange={handleRegionFilterChange}
          onAccountChange={handleAccountFilterChange}
          onPaymentStatusChange={handlePaymentStatusFilterChange}
          onDateRangeChange={handleDateRangeFilterChange}
          onProjectTypeChange={handleProjectTypeFilterChange}
          onApplyFilters={handleApplyAllFilters}
          onClearAll={handleClearAllFilters}
        />

        {/* Table View */}
        <div className="rounded-lg">
          <OrderTable
            isLoading={isLoading}
            orders={orders}
            current={queryParams.current}
            pageSize={queryParams.pageSize}
            total={orderListResponse?.['@odata.count']}
            nextLink={orderListResponse?.['@odata.nextLink']}
            totalRecordCountLimitExceeded={orderListResponse?.['@Microsoft.Dynamics.CRM.totalrecordcountlimitexceeded']}
            onPageChange={handleTableChange}
onChangeOrderStatus={handleOpenOrderStatusModal}
            onDownloadPDF={handleDownloadPDF}
            onResendPaymentLink={handleOpenResendPaymentModal}
            onEditEvolutionJobNumber={handleOpenEvolutionJobModal}
          />
        </div>


      {/* Modals */}

<OrderStatusModal
        open={orderStatusModalOpen}
        order={selectedOrder}
        onCancel={handleCloseOrderStatusModal}
        onConfirm={handleUpdateOrderStatus}
        loading={updateOrderMutation.isPending || fulfillSalesOrderMutation.isPending || cancelSalesOrderMutation.isPending}
      />

      <EvolutionJobNumberModal
        open={evolutionJobModalOpen}
        order={selectedOrder}
        onCancel={handleCloseEvolutionJobModal}
        onConfirm={handleSaveEvolutionJobNumber}
        loading={updateOrderMutation.isPending}
      />

      <ResendPaymentLinkModal
        open={resendPaymentModalOpen}
        onClose={() => { setResendPaymentModalOpen(false); setSelectedOrder(null); }}
        onConfirm={handleResendPaymentLink}
        isLoading={isSendingPayment}
      />

      {/* PDF Generation Progress Modal */}
      <Modal
        open={pdfProgressModal}
        footer={null}
        closable={false}
        centered
        width={320}
      >
        <div className="flex flex-col items-center gap-4 py-4">
          <Spin size="large" />
          <p className="text-sm text-gray-600">{pdfProgressText}</p>
        </div>
      </Modal>
    </div>
  );
};

export default OrdersManagementPage;
